import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, ChevronDown, ChevronRight, Download, FileAudio, ListChecks, Pencil, Plus, Trash2 } from 'lucide-react';
import type { Job, JobItem, JobStatus, Transcription, UpdateJobInput } from '@portal/shared';
import type { Template } from '../../lib/stt-api.js';
import { useDictationFlow } from './dictation-uploader.js';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Modal,
  Select,
  Spinner,
  Table,
  Textarea,
  useToast,
  type TableColumn,
} from '../../components/index.js';
import { useAuth } from '../../lib/auth-context.js';
import { api, ApiError } from '../../lib/api-client.js';
import { firebase as fb } from '../../lib/firebase.js';
import { track } from '../../lib/analytics/index.js';
import { hasAtLeastRole, type PricingBatch } from '@portal/shared';
import { PricingCard } from './job-pricing.js';

const STATUS_VARIANT: Record<JobStatus, 'neutral' | 'info' | 'success' | 'accent' | 'warning'> = {
  draft: 'neutral',
  active: 'info',
  priced: 'success',
  delivered: 'accent',
  archived: 'warning',
};

const STATUS_OPTIONS: JobStatus[] = ['draft', 'active', 'priced', 'delivered', 'archived'];

interface JobWithTranscriptions extends Job {
  transcriptions?: Transcription[];
  pricingBatches?: PricingBatch[];
}

export function JobDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const { me } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobWithTranscriptions | null>(null);
  const [items, setItems] = useState<JobItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusPending, setStatusPending] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [txDeleting, setTxDeleting] = useState<Transcription | null>(null);
  const [txDeletePending, setTxDeletePending] = useState(false);

  const canEdit = useMemo(() => {
    if (!me || !job) return false;
    if (hasAtLeastRole(me.role, 'admin')) return true;
    return me.role === 'staff' && job.assignedTo === me.id;
  }, [me, job]);

  const canDelete = !!me && hasAtLeastRole(me.role, 'admin');

  // Ref tracks whether we've ever loaded successfully — distinguishes the
  // initial load (where a failure should show an error card) from a
  // background refresh (where a failure should leave the existing view
  // intact rather than wiping it).
  const hasLoadedRef = useRef(false);
  const refresh = useCallback(async () => {
    try {
      const [jobRes, itemsRes] = await Promise.all([
        api.get<{ job: JobWithTranscriptions }>(`/api/jobs/${id}`),
        api.get<{ items: JobItem[] }>(`/api/jobs/${id}/items`),
      ]);
      setJob(jobRes.job);
      setItems(itemsRes.items);
      setError(null);
      hasLoadedRef.current = true;
    } catch (err) {
      const msg = err instanceof ApiError || err instanceof Error ? err.message : String(err);
      if (!hasLoadedRef.current) {
        setError(msg);
        setJob(null);
        setItems(null);
      } else {
        // Background refresh failed — keep stale view, log for debugging.
        console.error('Job refresh failed:', msg);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Items-only refresh — used by the Pricing card after batch start/finish/
  // cancel. Skips refetching the job document, which means the header /
  // dictation card / status / transcriptions don't get touched, so React
  // doesn't reconcile half the page just to surface new pricing data.
  const refreshItems = useCallback(async () => {
    try {
      const itemsRes = await api.get<{ items: JobItem[] }>(`/api/jobs/${id}/items`);
      setItems(itemsRes.items);
    } catch (err) {
      const msg = err instanceof ApiError || err instanceof Error ? err.message : String(err);
      console.error('Items refresh failed:', msg);
    }
  }, [id]);

  const hasTranscription = !!job?.transcriptions && job.transcriptions.length > 0;

  const onDownloadXlsx = useCallback(async () => {
    if (!job) return;
    setDownloading(true);
    try {
      // Fetch the binary stream from the API ourselves so we can attach the
      // Authorization header — <a href> can't do that. Pure click-through to
      // /api/jobs/:id/download.xlsx would 401.
      const token = await fb().auth.currentUser?.getIdToken();
      // stage=dictation → xlsx with greg_value (Greg's dictated values),
      // engine pricing decisions + overrides ignored. Snapshot of the
      // transcription stage of the pipeline.
      const res = await fetch(`/api/jobs/${job.id}/download.xlsx?stage=dictation`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!res.ok) throw new Error(`download failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${job.business.replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 80)}-dictation.xlsx`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setDownloading(false);
    }
  }, [job, toast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onStatusChange = async (next: JobStatus) => {
    if (!job || next === job.status) return;
    setStatusPending(true);
    try {
      await api.patch(`/api/jobs/${job.id}`, { status: next });
      toast({ title: `Status: ${next}`, variant: 'success' });
      await refresh();
    } catch (err) {
      toast({
        title: 'Status change failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setStatusPending(false);
    }
  };

  const onDeleteTranscription = async () => {
    if (!txDeleting || !job) return;
    setTxDeletePending(true);
    try {
      const res = await api.delete<{ removedItems: number }>(
        `/api/jobs/${job.id}/transcriptions/${encodeURIComponent(txDeleting.sttTaskId)}`,
      );
      toast({
        title: 'Transcription removed',
        description: `${res.removedItems} item${res.removedItems === 1 ? '' : 's'} cleared. Audio file stays in GCS.`,
        variant: 'success',
      });
      setTxDeleting(null);
      await refresh();
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setTxDeletePending(false);
    }
  };

  const onDelete = async () => {
    if (!job) return;
    setDeletePending(true);
    try {
      await api.delete(`/api/jobs/${job.id}`);
      toast({ title: `Removed ${job.business}`, variant: 'success' });
      navigate('/tools/jobs');
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
      setDeletePending(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-8 py-10 space-y-6">
        <Breadcrumb business={job?.business} />

        {loading && (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        )}

        {!loading && error && (
          <Card>
            <CardBody>
              <div role="alert" className="text-danger">
                Could not load job: {error}
              </div>
              <div className="mt-3">
                <Link
                  to="/tools/jobs"
                  className="inline-flex items-center gap-1 text-sm text-accent-base hover:text-accent-hover"
                >
                  <ArrowLeft className="size-4" aria-hidden />
                  Back to jobs
                </Link>
              </div>
            </CardBody>
          </Card>
        )}

        {!loading && job && (
          <>
            <header className="flex items-center justify-between gap-6">
              <div className="flex items-center gap-3 min-w-0">
                <span className="inline-flex size-10 items-center justify-center rounded-lg bg-accent-muted text-accent-hover shrink-0">
                  <Briefcase className="size-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold text-text-primary leading-tight truncate">
                    {job.business}
                  </h1>
                  {job.client && job.client !== job.business && (
                    <p className="text-sm text-text-secondary leading-tight truncate mt-0.5">
                      {job.client}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {canEdit ? (
                  <Select
                    aria-label="Change status"
                    value={job.status}
                    disabled={statusPending}
                    onChange={(e) => void onStatusChange(e.currentTarget.value as JobStatus)}
                    className="text-sm h-8 min-h-0 py-0"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Badge variant={STATUS_VARIANT[job.status]} dot>
                    {job.status}
                  </Badge>
                )}
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Trash2 className="size-4" aria-hidden />}
                    onClick={() => setDeleteOpen(true)}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </header>

            <Card>
              <CardHeader
                title="Details"
                actions={
                  canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Pencil className="size-4" aria-hidden />}
                      onClick={() => setEditOpen(true)}
                    >
                      Edit
                    </Button>
                  )
                }
              />
              <CardBody className="space-y-3">
                <DetailRow label="Address" value={job.address || '—'} />
                <DetailRow
                  label="Created"
                  value={new Date(job.createdAt).toLocaleString()}
                />
                <DetailRow
                  label="Updated"
                  value={new Date(job.updatedAt).toLocaleString()}
                />
                {job.notes && <DetailRow label="Notes" value={<span className="whitespace-pre-wrap">{job.notes}</span>} />}
              </CardBody>
            </Card>

            <InventoryCard
              job={job}
              items={items}
              canEdit={canEdit}
              downloading={downloading}
              onDownloadXlsx={onDownloadXlsx}
              onItemChange={(itemId, patch) => {
                setItems((prev) =>
                  prev ? prev.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) : prev,
                );
              }}
              onAttachTranscription={async (sttTaskId) => {
                await api.post(`/api/jobs/${job.id}/transcriptions`, { sttTaskId });
                track.interaction('transcription_attached_via_ui', {
                  jobId: job.id,
                  targetType: 'transcription',
                  targetId: sttTaskId,
                });
                toast({ title: 'Recording attached', variant: 'success' });
                await refresh();
              }}
              onDeleteTranscription={setTxDeleting}
              onItemsChanged={refreshItems}
            />

            <PricingCard
              jobId={job.id}
              items={items ?? []}
              batches={job.pricingBatches ?? []}
              canEdit={canEdit}
              onChange={refreshItems}
              onBatchStart={refresh}
              onItemsPatch={(fresh) => setItems(fresh)}
            />
          </>
        )}
      </div>

      {editOpen && job && (
        <EditJobModal
          job={job}
          onClose={() => setEditOpen(false)}
          onSaved={async () => {
            toast({ title: 'Details updated', variant: 'success' });
            setEditOpen(false);
            await refresh();
          }}
        />
      )}

      {deleteOpen && job && (
        <Modal
          open
          onClose={() => (deletePending ? undefined : setDeleteOpen(false))}
          title="Delete job?"
          description={`${job.business} and all its data will be permanently deleted. This cannot be undone.`}
          footer={
            <>
              <Button variant="secondary" onClick={() => setDeleteOpen(false)} disabled={deletePending}>
                Cancel
              </Button>
              <Button variant="danger" onClick={onDelete} loading={deletePending}>
                Delete job
              </Button>
            </>
          }
        />
      )}

      {txDeleting && (
        <Modal
          open
          onClose={() => (txDeletePending ? undefined : setTxDeleting(null))}
          title="Delete this transcription?"
          description={
            `Removes ${txDeleting.filenames[0] ?? txDeleting.sttTaskId} and the ${txDeleting.itemCount} line item${txDeleting.itemCount === 1 ? '' : 's'} it produced. ` +
            `Pricing decisions on those items are also lost. The original audio file stays archived in GCS. ` +
            `You can then re-dictate a fresh recording from "Start dictation".`
          }
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setTxDeleting(null)}
                disabled={txDeletePending}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={onDeleteTranscription} loading={txDeletePending}>
                Delete transcription
              </Button>
            </>
          }
        />
      )}
    </AppShell>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <div className="w-32 text-sm text-text-secondary shrink-0">{label}</div>
      <div className="text-text-primary min-w-0 flex-1">{value}</div>
    </div>
  );
}

interface InventoryCardProps {
  job: JobWithTranscriptions;
  items: JobItem[] | null;
  canEdit: boolean;
  downloading: boolean;
  onDownloadXlsx: () => void | Promise<void>;
  onAttachTranscription: (sttTaskId: string) => Promise<void>;
  onDeleteTranscription: (t: Transcription) => void;
  onItemChange: (itemId: string, patch: Partial<JobItem>) => void;
  /** Full items-list refresh — used after structural changes (add/delete row). */
  onItemsChanged: () => void | Promise<void>;
}

function InventoryCard({
  job,
  items,
  canEdit,
  downloading,
  onDownloadXlsx,
  onAttachTranscription,
  onDeleteTranscription,
  onItemChange,
  onItemsChanged,
}: InventoryCardProps) {
  const [itemsExpanded, setItemsExpanded] = useState(false);
  const hasTranscription = !!job.transcriptions && job.transcriptions.length > 0;

  const itemSummary = useMemo(() => {
    if (!items) return { total: 0, priced: 0 };
    const assets = items.filter((it) => !it.isHeader);
    return {
      total: assets.length,
      priced: assets.filter((it) => {
        const v = it.acceptedPrice ?? it.greg_value;
        return v != null && v > 0;
      }).length,
    };
  }, [items]);

  // Card-level summary line: rolls up both transcription count and item count.
  let description: string;
  if (!hasTranscription) {
    description = 'No recordings yet — start a dictation session and items land here.';
  } else {
    const txCount = job.transcriptions!.length;
    const txLabel = `${txCount} recording${txCount === 1 ? '' : 's'}`;
    if (itemSummary.total === 0) {
      description = txLabel;
    } else {
      description = `${txLabel} · ${itemSummary.total} item${itemSummary.total === 1 ? '' : 's'}, ${itemSummary.priced} priced`;
    }
  }

  // Engine only ships going_concern and liquidation xlsx templates. We default
  // to going_concern (Greg's near-universal choice — OLV + FLV are derived
  // from a going_concern dictation via his own formulas, not a separate xlsx).
  // The user can override per-upload via the toggle on the uploader if a job
  // is genuinely a liquidation appraisal.
  const [template, setTemplate] = useState<Template>('going_concern');

  const flow = useDictationFlow({
    template,
    onTemplateChange: setTemplate,
    onComplete: async ({ sttTaskId }) => {
      await onAttachTranscription(sttTaskId);
    },
    ctaLabel: hasTranscription ? 'Drop another recording' : 'Drop a recording to begin',
    compact: true,
  });

  return (
    <Card>
      <CardHeader title="Dictation" description={description} />
      <CardBody className="space-y-0 p-0">
        {/* 1. UPLOAD — dropzone, file list, progress */}
        {canEdit && (
          <div className="px-6 pt-4 pb-3 space-y-3">
            {flow.dropzone}
            {flow.fileList}
            {flow.progress}
            {flow.error}
          </div>
        )}

        {/* 2. SHOW UPLOADS — past transcriptions */}
        {hasTranscription && (
          <ul className="divide-y divide-border-subtle border-t border-border-subtle px-6 py-2">
            {job.transcriptions!.map((t) => (
              <li key={t.sttTaskId} className="flex items-center gap-3 py-3">
                <FileAudio className="size-4 text-accent-base shrink-0" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="text-text-primary truncate">
                    {t.filenames.length > 0 ? t.filenames.join(', ') : t.sttTaskId}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {new Date(t.addedAt).toLocaleString()} ·{' '}
                    {t.filenames.length > 1 ? `${t.filenames.length} recordings · ` : ''}
                    {t.itemCount} items
                    {t.template ? ` · ${t.template}` : ''}
                  </div>
                </div>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Trash2 className="size-4" aria-hidden />}
                    onClick={() => onDeleteTranscription(t)}
                    title="Delete this transcription and its items"
                  >
                    Delete
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* 3. SHOW LINE ITEMS — collapsible toggle */}
        {itemSummary.total > 0 && (
          <div className="border-t border-border-subtle">
            <button
              type="button"
              onClick={() => setItemsExpanded((v) => !v)}
              className="w-full flex items-center justify-between gap-3 px-6 py-3 text-left hover:bg-bg-surface-2 transition-colors"
              aria-expanded={itemsExpanded}
            >
              <div className="flex items-center gap-2 text-sm">
                <ListChecks className="size-4 text-text-secondary" aria-hidden />
                <span className="text-text-primary font-medium">Line items</span>
                <span className="text-text-tertiary">
                  {itemSummary.total} · {itemSummary.priced} priced
                </span>
              </div>
              <div className="text-text-secondary shrink-0">
                {itemsExpanded ? (
                  <ChevronDown className="size-4" aria-hidden />
                ) : (
                  <ChevronRight className="size-4" aria-hidden />
                )}
              </div>
            </button>
            {itemsExpanded && items && (
              <div className="border-t border-border-subtle">
                <ItemsTable
                  jobId={job.id}
                  items={items}
                  canEdit={canEdit}
                  onItemChange={onItemChange}
                  onItemsChanged={onItemsChanged}
                />
              </div>
            )}
          </div>
        )}

        {/* 4. TAKE ACTION — template toggle + transcribe + download xlsx */}
        {canEdit && (
          <div className="border-t border-border-subtle px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              {flow.templateToggle}
              {flow.filesReadyText && (
                <span className="text-xs text-text-tertiary truncate">{flow.filesReadyText}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasTranscription && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Download className="size-4" aria-hidden />}
                  loading={downloading}
                  onClick={onDownloadXlsx}
                >
                  Download xlsx
                </Button>
              )}
              {flow.transcribeButton}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Editable line-items table for the Dictation card. Lets Greg fix STT
 *  mistakes inline:
 *   - description (text), qty (number), value (number) all autosave on blur
 *     or Enter; Escape reverts the cell.
 *   - ArrowUp/ArrowDown jumps to the same column in the previous/next row;
 *     Tab/Shift-Tab walks horizontally (browser default for inputs).
 *   - Header rows render bold, only their description is editable. */
type EditableField = 'qty' | 'description' | 'greg_value';
const FIELD_ORDER: EditableField[] = ['qty', 'description', 'greg_value'];

function ItemsTable({
  jobId,
  items,
  canEdit,
  onItemChange,
  onItemsChanged,
}: {
  jobId: string;
  items: JobItem[];
  canEdit: boolean;
  onItemChange: (itemId: string, patch: Partial<JobItem>) => void;
  onItemsChanged: () => void | Promise<void>;
}) {
  // 2D refs grid for arrow-key nav: [rowIdx][colIdx → field index in FIELD_ORDER]
  const cellsRef = useRef<Array<Array<HTMLInputElement | null>>>([]);

  const focusCell = (rowIdx: number, fieldIdx: number) => {
    const row = cellsRef.current[rowIdx];
    if (!row) return;
    const el = row[fieldIdx];
    if (el && !el.disabled) {
      el.focus();
      el.select();
    }
  };

  const onCellKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
    rowIdx: number,
    fieldIdx: number,
  ) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Find next row that has a non-disabled cell in this field; skip headers
      for (let r = rowIdx + 1; r < items.length; r++) {
        const el = cellsRef.current[r]?.[fieldIdx];
        if (el && !el.disabled) {
          focusCell(r, fieldIdx);
          return;
        }
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      for (let r = rowIdx - 1; r >= 0; r--) {
        const el = cellsRef.current[r]?.[fieldIdx];
        if (el && !el.disabled) {
          focusCell(r, fieldIdx);
          return;
        }
      }
    }
    // Enter and Escape are handled by the EditableCell itself.
  };

  const { toast } = useToast();

  const onAddRow = async () => {
    try {
      await api.post(`/api/jobs/${jobId}/items`, {});
      await onItemsChanged();
    } catch (err) {
      toast({
        title: 'Failed to add item',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    }
  };

  const onDeleteRow = async (it: JobItem) => {
    if (!window.confirm(`Delete "${it.description || 'this item'}"?`)) return;
    try {
      await api.delete(`/api/jobs/${jobId}/items/${it.id}`);
      await onItemsChanged();
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    }
  };

  return (
    <>
    <table className="w-full border-collapse table-fixed text-sm">
      <thead className="bg-bg-surface-2 border-b border-border-subtle">
        <tr>
          <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary uppercase w-20">Qty</th>
          <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary uppercase">Description</th>
          <th className="px-3 py-2 text-right text-xs font-semibold text-text-secondary uppercase w-32">Value</th>
          <th className="w-12" aria-hidden></th>
        </tr>
      </thead>
      <tbody>
        {items.map((it, rowIdx) => {
          // Allocate the row's cell slot up-front
          if (!cellsRef.current[rowIdx]) cellsRef.current[rowIdx] = [null, null, null];
          return (
            <tr key={it.id} className="border-b border-border-subtle last:border-0">
              <td className="px-3 py-2 align-middle">
                {!it.isHeader && (
                  <EditableCell
                    setRef={(el) => (cellsRef.current[rowIdx]![0] = el)}
                    initial={it.qty != null ? String(it.qty) : ''}
                    type="number"
                    align="right"
                    disabled={!canEdit}
                    onKeyDown={(e) => onCellKeyDown(e, rowIdx, 0)}
                    tracking={{ jobId, itemId: it.id, field: 'qty' }}
                    onSave={async (raw) => {
                      const trimmed = raw.trim();
                      const next = trimmed === '' ? null : Number(trimmed);
                      if (next != null && !Number.isFinite(next)) return false;
                      if ((next ?? null) === (it.qty ?? null)) return true;
                      try {
                        await api.patch(`/api/jobs/${jobId}/items/${it.id}/dictation`, { qty: next });
                        onItemChange(it.id, { qty: next ?? undefined });
                        return true;
                      } catch {
                        return false;
                      }
                    }}
                  />
                )}
              </td>
              <td className="px-3 py-2 align-middle">
                {it.isHeader ? (
                  <span className="font-semibold uppercase text-xs tracking-wide text-text-tertiary">
                    {it.description ?? ''}
                  </span>
                ) : (
                  <div className="flex items-center gap-2 min-w-0">
                    <EditableCell
                      setRef={(el) => (cellsRef.current[rowIdx]![1] = el)}
                      initial={it.description ?? ''}
                      type="text"
                      disabled={!canEdit}
                      onKeyDown={(e) => onCellKeyDown(e, rowIdx, 1)}
                      tracking={{ jobId, itemId: it.id, field: 'description' }}
                      onSave={async (raw) => {
                        const trimmed = raw.trim();
                        if (trimmed === '') return false;
                        if (trimmed === (it.description ?? '')) return true;
                        try {
                          await api.patch(`/api/jobs/${jobId}/items/${it.id}/dictation`, { description: trimmed });
                          onItemChange(it.id, { description: trimmed });
                          return true;
                        } catch {
                          return false;
                        }
                      }}
                    />
                    {it.isLeasehold && <Badge variant="warning">Leasehold</Badge>}
                  </div>
                )}
              </td>
              <td className="px-3 py-2 align-middle">
                {!it.isHeader && (
                  <EditableCell
                    setRef={(el) => (cellsRef.current[rowIdx]![2] = el)}
                    initial={it.greg_value != null ? String(it.greg_value) : ''}
                    type="number"
                    align="right"
                    disabled={!canEdit}
                    onKeyDown={(e) => onCellKeyDown(e, rowIdx, 2)}
                    tracking={{ jobId, itemId: it.id, field: 'greg_value' }}
                    onSave={async (raw) => {
                      const trimmed = raw.trim();
                      const next = trimmed === '' ? null : Number(trimmed);
                      if (next != null && !Number.isFinite(next)) return false;
                      if ((next ?? null) === (it.greg_value ?? null)) return true;
                      try {
                        await api.patch(`/api/jobs/${jobId}/items/${it.id}/dictation`, { greg_value: next });
                        onItemChange(it.id, { greg_value: next ?? undefined });
                        return true;
                      } catch {
                        return false;
                      }
                    }}
                  />
                )}
              </td>
              <td className="px-2 py-2 align-middle text-center">
                {canEdit && (
                  <button
                    type="button"
                    title="Delete this item"
                    aria-label="Delete item"
                    className="text-text-tertiary hover:text-danger transition-colors"
                    onClick={() => void onDeleteRow(it)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    {canEdit && (
      <div className="border-t border-border-subtle px-3 py-2">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Plus className="size-4" aria-hidden />}
          onClick={() => void onAddRow()}
        >
          Add item
        </Button>
      </div>
    )}
    </>
  );
}

/** A single editable cell. Commits onBlur or Enter; reverts on Escape.
 *  Returns true from onSave to keep the new value, false to revert.
 *
 *  `tracking` is optional — when present, the cell emits item_edit_* events
 *  so we can measure how often Greg starts an edit and how many he commits
 *  vs. cancels. The audit_log already captures the *committed* state via
 *  job.item.edit; these client events add the intent + cancellation signal. */
function EditableCell({
  initial,
  type,
  align,
  disabled,
  setRef,
  onSave,
  onKeyDown,
  tracking,
}: {
  initial: string;
  type: 'text' | 'number';
  align?: 'right';
  disabled?: boolean;
  setRef: (el: HTMLInputElement | null) => void;
  onSave: (raw: string) => Promise<boolean>;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  tracking?: { jobId: string; itemId: string; field: 'description' | 'qty' | 'greg_value' };
}) {
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const committedRef = useRef(initial);
  // Sync external changes (e.g. parent re-fetched items) when not actively editing
  useEffect(() => {
    if (!saving) {
      setValue(initial);
      committedRef.current = initial;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const commit = async () => {
    if (saving) return;
    if (value === committedRef.current) return;
    setSaving(true);
    const ok = await onSave(value);
    setSaving(false);
    if (ok) {
      committedRef.current = value;
      if (tracking) {
        track.interaction('item_edit_committed', {
          jobId: tracking.jobId,
          targetType: 'item',
          targetId: tracking.itemId,
          extra: { field: tracking.field },
        });
      }
    } else {
      setValue(committedRef.current);
    }
  };

  return (
    <input
      ref={setRef}
      type={type}
      value={value}
      disabled={disabled || saving}
      onChange={(e) => setValue(e.currentTarget.value)}
      onFocus={(e) => {
        e.currentTarget.select();
        if (tracking) {
          track.interaction('item_edit_opened', {
            jobId: tracking.jobId,
            targetType: 'item',
            targetId: tracking.itemId,
            extra: { field: tracking.field },
          });
        }
      }}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void commit();
          (e.currentTarget as HTMLInputElement).blur();
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setValue(committedRef.current);
          // Fire BEFORE blur — blur runs commit() synchronously and would
          // otherwise emit item_edit_committed (and order events wrong).
          if (tracking) {
            track.interaction('item_edit_cancelled', {
              jobId: tracking.jobId,
              targetType: 'item',
              targetId: tracking.itemId,
              extra: { field: tracking.field },
            });
          }
          (e.currentTarget as HTMLInputElement).blur();
          return;
        }
        onKeyDown(e);
      }}
      className={
        'w-full bg-transparent px-1.5 py-1 rounded border border-transparent ' +
        'hover:border-border-subtle focus:border-accent-base focus:bg-bg-surface-1 ' +
        'focus:outline-none text-text-primary tabular-nums ' +
        (align === 'right' ? 'text-right ' : '') +
        (saving ? 'opacity-50 ' : '')
      }
    />
  );
}

interface EditJobModalProps {
  job: Job;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function EditJobModal({ job, onClose, onSaved }: EditJobModalProps) {
  const { toast } = useToast();
  const [client, setClient] = useState(job.client ?? '');
  const [business, setBusiness] = useState(job.business);
  const [address, setAddress] = useState(job.address ?? '');
  const [notes, setNotes] = useState(job.notes ?? '');
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Send only what's changed — keeps audit log payload meaningful.
      const patch: UpdateJobInput = {};
      if (client.trim() !== (job.client ?? '')) patch.client = client.trim() || undefined;
      if (business.trim() !== job.business) patch.business = business.trim();
      const trimmedAddr = address.trim();
      if (trimmedAddr !== (job.address ?? '')) patch.address = trimmedAddr || undefined;
      const trimmedNotes = notes.trim();
      if (trimmedNotes !== (job.notes ?? '')) patch.notes = trimmedNotes || undefined;

      if (Object.keys(patch).length === 0) {
        toast({ title: 'No changes to save', variant: 'info' });
        onClose();
        return;
      }
      await api.patch(`/api/jobs/${job.id}`, patch);
      await onSaved();
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (saving ? undefined : onClose())}
      title="Edit job details"
      description="Updates are tracked in the audit log."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button form="edit-job-form" type="submit" loading={saving}>
            Save changes
          </Button>
        </>
      }
    >
      <form id="edit-job-form" onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Business"
          required
          value={business}
          onChange={(e) => setBusiness(e.currentTarget.value)}
        />
        <Input
          label="Client"
          placeholder="Optional — bank or referrer"
          value={client}
          onChange={(e) => setClient(e.currentTarget.value)}
        />
        <Input
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.currentTarget.value)}
        />
        <Textarea
          label="Notes"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
      </form>
    </Modal>
  );
}

function Breadcrumb({ business }: { business?: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-text-secondary min-w-0">
      <Link to="/dashboard" className="hover:text-text-primary">
        Dashboard
      </Link>
      <ChevronRight className="size-4 text-text-tertiary shrink-0" aria-hidden />
      <Link to="/tools/jobs" className="hover:text-text-primary">
        Jobs
      </Link>
      {business && (
        <>
          <ChevronRight className="size-4 text-text-tertiary shrink-0" aria-hidden />
          <span className="text-text-primary truncate">{business}</span>
        </>
      )}
    </nav>
  );
}
